import { lazy } from "react";

import { envChecker } from "helpers/envChecker";

import { RouteType } from "types";
import { routePaths } from "./constants";
import { APP_ACCESS_RIGHT } from "constants/accessRight";
import { EENV } from "constants/index";

const Login = lazy(() => import("pages/Login/View"));
const Kanban = lazy(() => import("pages/Kanban/View"));
const TicketListView = lazy(() => import("pages/TicketListView/View"));

const Dashboard = lazy(() => import("pages/Dashboard/View"));
const PageNotFound = lazy(() => import("pages/404"));
const StaffAssignedAccountList = lazy(
  () => import("pages/AssignedAccount/List/Staff/View")
);
const ClientAssignedAccountList = lazy(
  () => import("pages/AssignedAccount/List/Client/View")
);
const CorporationAssignedAccountList = lazy(
  () => import("pages/AssignedAccount/List/Corporation/View")
);
const CompanyMatchingList = lazy(
  () => import("pages/Recruitment/CompanyMatchingList/View")
);
const StaffMatchingList = lazy(
  () => import("pages/Recruitment/StaffMatchingList/View")
);

const TodaysEODReportTicket = lazy(
  () => import("pages/Reports/TodaysReport/Ticket")
);
const TodaysReportingLine = lazy(
  () => import("pages/Reports/TodaysReport/ReportingLine")
);

const EodHistoryView = lazy(() => import("pages/Reports/EodHistory/View"));

const EodHistoryReportReportingLine = lazy(
  () => import("pages/Reports/EodHistory/Report/ReportingLine")
);
const EodHistoryReportTickets = lazy(
  () => import("pages/Reports/EodHistory/Report/Ticket")
);

const PendingEODReportTicketsView = lazy(
  () => import("pages/Reports/PendingEOD/Ticket/View")
);

const PendingEODReportReportingLineView = lazy(
  () => import("pages/Reports/PendingEOD/ReportingLine/View")
);

const Home = lazy(() => import("pages/Home/View"));

const AIJobSuggestion = lazy(
  () => import("pages/AssignedAccount/CandidateDetail/AIJobSuggestion/View")
);
const ManualJobSuggestion = lazy(
  () => import("pages/AssignedAccount/CandidateDetail/ManualJobSuggestion/View")
);

const MembersView = lazy(() => import("pages/Members/View"));

export const routes: RouteType[] = [
  {
    path: routePaths.root.root,
    requiredAuth: true,
    element: <Home />,
    hideBreadcrumb: true,
    withoutLayout: true,
  },
  {
    path: routePaths.root.pBuSlug.root,
    requiredAuth: true,
    redirect: routePaths.root.pBuSlug.dashboard.root,
    element: null,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.pBuSlug.dashboard.root,
    requiredAuth: true,
    element: <Dashboard />,
    label: "dashboard",
    redirect: envChecker({ envs: [EENV.DEV, EENV.QA] })
      ? undefined
      : routePaths.root.pBuSlug.assignedAccount.root,
  },
  {
    path: routePaths.root.pBuSlug.assignedAccount.root,
    redirect: routePaths.root.pBuSlug.assignedAccount.staff.root,
    requiredAuth: true,
    element: null,
    label: "assigned_account",
  },
  {
    redirect: routePaths.root.pBuSlug.manageTask.kanbanView.root,
    path: routePaths.root.pBuSlug.manageTask.root,
    requiredAuth: true,
    element: null,
    label: "manage_tasks",
  },
  {
    path: routePaths.root.pBuSlug.assignedAccount.candidateDetail.root,
    redirect:
      routePaths.root.pBuSlug.assignedAccount.candidateDetail
        .manualJobSuggestion.root,
    requiredAuth: true,
    element: null,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.pBuSlug.assignedAccount.candidateDetail
      .aiJobSuggestion.root,
    requiredAuth: true,
    element: <AIJobSuggestion />,
    label: "candidate_detail_page",
    requiredAccessRights: [APP_ACCESS_RIGHT.AI_MATCHING_VIEW],
  },
  {
    path: routePaths.root.pBuSlug.assignedAccount.candidateDetail
      .manualJobSuggestion.root,
    requiredAuth: true,
    element: <ManualJobSuggestion />,
    label: "candidate_detail_page",
  },
  {
    path: routePaths.root.pBuSlug.assignedAccount.staff.root,
    requiredAuth: true,
    element: <StaffAssignedAccountList />,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.pBuSlug.assignedAccount.client.root,
    requiredAuth: true,
    element: <ClientAssignedAccountList />,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.pBuSlug.assignedAccount.corporation.root,
    requiredAuth: true,
    element: <CorporationAssignedAccountList />,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.pBuSlug.recruitment.root,
    requiredAuth: true,
    redirect: routePaths.root.pBuSlug.recruitment.companyMatchingList.root,
    element: null,
    label: "recruitment",
  },
  {
    path: routePaths.root.pBuSlug.recruitment.companyMatchingList.root,
    requiredAuth: true,
    element: <CompanyMatchingList />,
    label: "company_matching_list",
    requiredAccessRights: [APP_ACCESS_RIGHT.AI_MATCHING_VIEW],
  },
  {
    path: routePaths.root.pBuSlug.recruitment.staffMatchingList.root,
    requiredAuth: true,
    element: <StaffMatchingList />,
    label: "staff_matching_list",
    requiredAccessRights: [APP_ACCESS_RIGHT.AI_MATCHING_VIEW],
  },
  {
    path: routePaths.root.pBuSlug.eodReport.root,
    requiredAuth: true,
    redirect: routePaths.root.pBuSlug.eodReport.todaysReport.root,
    element: null,
    label: "reports",
    requiredAccessRights: [
      APP_ACCESS_RIGHT.EOD_MANAGE_ALL_EOD,
      APP_ACCESS_RIGHT.EOD_MANAGE_ASSIGNED_EODS,
    ],
    redirectWhenPermissionDenied:
      routePaths.root.pBuSlug.eodReport.eodHistory.root,
  },
  {
    path: routePaths.root.pBuSlug.eodReport.todaysReport.root,
    requiredAuth: true,
    element: null,
    redirect: routePaths.root.pBuSlug.eodReport.todaysReport.tickets.root,
    label: "todays_report",
    requiredAccessRights: [
      APP_ACCESS_RIGHT.EOD_MANAGE_ALL_EOD,
      APP_ACCESS_RIGHT.EOD_MANAGE_ASSIGNED_EODS,
    ],
    redirectWhenPermissionDenied:
      routePaths.root.pBuSlug.eodReport.eodHistory.root,
  },
  {
    path: routePaths.root.pBuSlug.eodReport.todaysReport.tickets.root,
    requiredAuth: true,
    element: <TodaysEODReportTicket />,
    hideBreadcrumb: true,
    requiredAccessRights: [
      APP_ACCESS_RIGHT.EOD_MANAGE_ALL_EOD,
      APP_ACCESS_RIGHT.EOD_MANAGE_ASSIGNED_EODS,
    ],
    redirectWhenPermissionDenied:
      routePaths.root.pBuSlug.eodReport.eodHistory.root,
  },
  {
    path: routePaths.root.pBuSlug.eodReport.todaysReport.reporting.root,
    requiredAuth: true,
    element: <TodaysReportingLine />,
    hideBreadcrumb: true,
    requiredAccessRights: [
      APP_ACCESS_RIGHT.EOD_MANAGE_ALL_EOD,
      APP_ACCESS_RIGHT.EOD_MANAGE_ASSIGNED_EODS,
    ],
  },
  {
    path: routePaths.root.pBuSlug.eodReport.eodHistory.root,
    requiredAuth: true,
    element: <EodHistoryView />,
    label: "eod_history",
    requiredAccessRights: [
      APP_ACCESS_RIGHT.EOD_MANAGE_ALL_EOD,
      APP_ACCESS_RIGHT.EOD_MANAGE_PAST_DUE_EOD,
    ],
  },
  {
    path: routePaths.root.pBuSlug.eodReport.eodHistory.report.root,
    requiredAuth: true,
    element: null,
    redirect: routePaths.root.pBuSlug.eodReport.eodHistory.report.tickets.root,
    label: "report",
    requiredAccessRights: [
      APP_ACCESS_RIGHT.EOD_MANAGE_ALL_EOD,
      APP_ACCESS_RIGHT.EOD_MANAGE_PAST_DUE_EOD,
    ],
  },
  {
    path: routePaths.root.pBuSlug.eodReport.eodHistory.report.reporting.root,
    requiredAuth: true,
    element: <EodHistoryReportReportingLine />,
    hideBreadcrumb: true,
    requiredAccessRights: [
      APP_ACCESS_RIGHT.EOD_MANAGE_ALL_EOD,
      APP_ACCESS_RIGHT.EOD_MANAGE_PAST_DUE_EOD,
    ],
  },
  {
    path: routePaths.root.pBuSlug.eodReport.eodHistory.report.tickets.root,
    requiredAuth: true,
    element: <EodHistoryReportTickets />,
    hideBreadcrumb: true,
    requiredAccessRights: [
      APP_ACCESS_RIGHT.EOD_MANAGE_ALL_EOD,
      APP_ACCESS_RIGHT.EOD_MANAGE_PAST_DUE_EOD,
    ],
  },
  {
    path: routePaths.root.pBuSlug.pendingReport.root,
    requiredAuth: true,
    element: null,
    redirect: routePaths.root.pBuSlug.pendingReport.tickets.root,
    label: "pending_report",
  },
  {
    path: routePaths.root.pBuSlug.pendingReport.tickets.root,
    requiredAuth: true,
    element: <PendingEODReportTicketsView />,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.pBuSlug.pendingReport.reporting.root,
    requiredAuth: true,
    element: <PendingEODReportReportingLineView />,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.pBuSlug.manageTask.kanbanView.root,
    requiredAuth: true,
    element: <Kanban />,
    label: "kanban_view",
    requiredAccessRights: [
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_STAFF_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_CLIENT_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_MATCH_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_AD_HOC_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_STAFF_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_CLIENT_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_MATCH_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_AD_HOC_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_LINKED_ASSIGNED_TICKETS,
    ],
  },
  {
    path: routePaths.root.pBuSlug.manageTask.listView.root,
    requiredAuth: true,
    element: <TicketListView />,
    label: "list_view",
    requiredAccessRights: [
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_STAFF_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_CLIENT_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_MATCH_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_AD_HOC_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_STAFF_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_CLIENT_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_MATCH_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_AD_HOC_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_LINKED_ASSIGNED_TICKETS,
    ],
  },
  {
    path: routePaths.root.pBuSlug.members.root,
    requiredAuth: true,
    element: <MembersView />,
    label: "members",
  },
  ...(envChecker({ envs: [EENV.DEV] })
    ? [
        {
          path: "/login",
          element: <Login />,
          requiredAuth: false,
          withoutLayout: true,
        },
      ]
    : []),

  // DEFAULT
  {
    path: "*",
    element: <PageNotFound />,
    requiredAuth: false,
    withoutLayout: true,
  },
];
